import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";

export default function GrowGuides() {
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    api.get("/grow-guides").then(({ data }) => setGuides(data)).catch(() => {});
  }, []);

  const formatVarietyTitle = (raw) => {
    const parts = raw.split(" - ").map(s => s.trim());
    const crop = parts[0].replace(/ Seeds$/, "");
    if (parts.length === 1) return crop;
    return [...parts.slice(1), crop].join(" ");
  };

  const grouped = useMemo(() => {
    return guides.reduce((acc, g) => {
      (acc[g.category] ||= []).push(g);
      return acc;
    }, {});
  }, [guides]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="grow-guides-page">
      <Seo
        title="How to Grow: Seed Growing Guides"
        description="Practical, climate-specific growing guides for every seed variety we carry: sun, soil, spacing, watering and harvest tips for California's Sierra foothills."
        path="/grow"
      />
      <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">The Field Notes</div>
      <h1 className="font-display text-5xl sm:text-6xl text-darkRoast">How to grow.</h1>
      <p className="mt-4 text-earth max-w-2xl leading-relaxed">
        Practical growing guides for every variety in our catalog, written for our
        climate here in the Sierra foothills. Soil, sun, spacing, harvest, and
        Amador-specific tips for each crop.
      </p>

      <div className="mt-16 space-y-16">
        {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([cat, items]) => (
          <section key={cat}>
            <div className="flex items-baseline justify-between mb-6 border-b border-darkRoast/10 pb-3">
              <h2 className="font-display text-3xl text-darkRoast">{cat}</h2>
              <span className="text-xs tracking-wider-2 uppercase text-earth">{items.length} varieties</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8">
              {items.map((g) => (
                <Link
                  key={g.product_id}
                  to={`/grow/${g.slug}`}
                  className="group flex gap-4 items-start"
                  data-testid={`guide-link-${g.slug}`}
                >
                  <div className="w-20 h-20 flex-shrink-0 bg-surface border border-darkRoast/10 rounded-sm overflow-hidden">
                    {g.image_url && <img src={g.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-lg text-darkRoast leading-tight group-hover:text-sage transition-colors">
                      How to grow {formatVarietyTitle(g.name)}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 text-xs tracking-wider-2 uppercase text-earth group-hover:text-sage">
                      <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Read guide <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
